// frontend/src/components/Project/ProjectDocuments.js - С ПОДДЕРЖКОЙ РЕЖИМА ТОЛЬКО ДЛЯ ПРОСМОТРА
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import DocumentTile from './DocumentTile';
import FileUpload from './FileUpload';
import { projectsAPI, API_BASE_URL } from '../../services/api';

const DocumentsContainer = styled(Paper)(({ theme }) => ({
  overflow: 'hidden',
  position: 'static',
  paddingTop: theme.spacing(11),
  paddingLeft: theme.spacing(4),
  paddingBottom: theme.spacing(3),
  paddingRight: theme.spacing(4),
  marginTop: theme.spacing(-11),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(4),
  background: 'linear-gradient(145deg, rgba(255,255,255,0.94) 0%, rgba(248,251,251,0.88) 100%)',
  border: '1px solid rgba(60,110,113,0.10)',
  boxShadow: '0 18px 45px rgba(32, 64, 66, 0.07), inset 0 1px 0 rgba(255,255,255,0.9)',
}));

const DocumentsGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  minHeight: '140px'
}));

const EmptyState = styled(Box)(({ theme }) => ({
  width: '100%',
  py: 4,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2)
}));

const ProjectDocuments = ({
  projectId,
  isOwner,
  isMember,
  isViewOnly = false,  // 🔥 НОВЫЙ ПРОПС
  onDocumentChange
}) => {
  const [documents, setDocuments] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);


  // Загрузка документов проекта
  useEffect(() => {
    if (projectId) {
      fetchDocuments();
    } else {
      setDocuments([]);
    }
  }, [projectId]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      console.log(`📥 Загрузка документов проекта ${projectId}`);
      const data = await projectsAPI.getDocuments(projectId);
      setDocuments(data || []);
    } catch (error) {
      console.error('Ошибка загрузки документов:', error);
      showNotification('Ошибка загрузки документов', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const handleUploadClick = () => {
    if (isViewOnly) {
      showNotification('В режиме просмотра нельзя загружать документы', 'info');
      return;
    }
    setUploadDialogOpen(true);
  };

  const handleUploadClose = () => {
    setUploadDialogOpen(false);
  };


  const handleFilesUploaded = async (newFiles) => {
    if (!newFiles || newFiles.length === 0) return;

    setLoading(true);

    try {
      // Загружаем каждый файл по отдельности
      for (const file of newFiles) {
        try {
          await projectsAPI.uploadDocument(projectId, file);
        } catch (uploadError) {
          console.error(`Ошибка загрузки файла ${file.name}:`, uploadError);
          showNotification(`Ошибка при загрузке ${file.name}`, 'error');
        }
      }

      // Обновляем список документов
      await fetchDocuments();

      showNotification(`Загружено ${newFiles.length} ${getFileWord(newFiles.length)}`, 'success');

      if (onDocumentChange) onDocumentChange();
    } catch (error) {
      console.error('Ошибка загрузки:', error);
      showNotification('Ошибка при загрузке файлов', 'error');
    } finally {
      setLoading(false);
      setUploadDialogOpen(false);
    }
  };

  // 🔥 ИСПРАВЛЕНО: скачивание с проверкой прав
  const handleDownload = (document) => {
    if (isViewOnly) {
      showNotification('В режиме просмотра нельзя скачивать файлы', 'info');
      return;
    }
    if (document.url) {
      window.open(document.url, '_blank');
    } else {
      showNotification('Файл временно недоступен', 'warning');
    }
  };

  // 🔥 ИСПРАВЛЕНО: удаление только для владельца/участника
  const handleDelete = async (document) => {
    if (isViewOnly) {
      showNotification('В режиме просмотра нельзя удалять файлы', 'info');
      return;
    }
    if (!window.confirm(`Удалить файл "${document.name}"?`)) return;

    try {
      setLoading(true);

      await projectsAPI.deleteDocument(document.id);

      // Обновляем список документов
      setDocuments(documents.filter(d => d.id !== document.id));
      showNotification('Файл удален', 'success');

      if (onDocumentChange) onDocumentChange();
    } catch (error) {
      console.error('Ошибка удаления:', error);
      showNotification('Ошибка при удалении файла', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 🔥 ИСПРАВЛЕНО: шаринг с предупреждением
  const handleShare = (document) => {
    if (isViewOnly) {
      showNotification('В режиме просмотра нельзя делиться файлами', 'info');
      return;
    }
    // Копируем ссылку в буфер обмена
    navigator.clipboard?.writeText(document.url || window.location.href);
    showNotification('Ссылка скопирована в буфер обмена', 'info');
  };

  const handleRefresh = () => {
    fetchDocuments();
  };

  const getFileWord = (count) => {
    if (count === 1) return 'файл';
    if (count > 1 && count < 5) return 'файла';
    return 'файлов';
  };

  const canUpload = (isOwner || isMember) && !isViewOnly;

  return (
    <>
      <DocumentsContainer elevation={0}>
        {/* Заголовок с кнопками */}
<Box
  sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2
  }}
>
  <Box>
    <Typography
      variant="h5"
      fontWeight={700}
      sx={{
        color: '#315A5D',
        letterSpacing: '-0.02em'
      }}
    >
      Документы проекта
    </Typography>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
      <Chip
        size="small"
        label={`${documents.length} ${getFileWord(documents.length)}`}
        sx={{
          height: 25,
          borderRadius: 99,
          px: 0.5,
          background: 'rgba(60,110,113,0.08)',
          color: '#466C6E',
          border: '1px solid rgba(60,110,113,0.10)',
          fontWeight: 600,
          '& .MuiChip-label': { px: 1.1 }
        }}
      />

      {isViewOnly && (
        <Chip
          size="small"
          icon={<VisibilityOffIcon sx={{ fontSize: '15px !important' }} />}
          label="Только просмотр"
          sx={{
            height: 25,
            borderRadius: 99,
            background: 'rgba(0,0,0,0.035)',
            color: 'text.secondary',
            border: '1px solid rgba(0,0,0,0.06)',
            '& .MuiChip-label': { px: 1 }
          }}
        />
      )}
    </Box>
  </Box>

  <Box sx={{ display: 'flex', gap: 1 }}>
    <Tooltip title="Обновить список">
      <span>
        <IconButton
          onClick={handleRefresh}
          disabled={loading}
          size="small"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '14px',
            background: 'rgba(255,255,255,0.72)',
            border: '1px solid rgba(60,110,113,0.10)',
            color: '#527477',
            transition: 'all .2s ease',
            '&:hover': {
              background: 'rgba(60,110,113,0.09)',
              transform: 'translateY(-1px)'
            }
          }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>

    {canUpload && (
      <Tooltip title="Загрузить документы">
        <span>
          <IconButton
            onClick={handleUploadClick}
            disabled={loading}
            sx={{
              width: 40,
              height: 40,
              borderRadius: '14px',
              background: 'linear-gradient(145deg, #4D7B7E 0%, #3C6E71 100%)',
              color: '#fff',
              boxShadow: '0 7px 18px rgba(60,110,113,0.20)',
              transition: 'all .2s ease',
              '&:hover': {
                background: 'linear-gradient(145deg, #416E71 0%, #315D60 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 10px 22px rgba(60,110,113,0.25)'
              }
            }}
          >
            <AddIcon />
          </IconButton>
        </span>
      </Tooltip>
    )}
  </Box>
</Box>

        {/* Сетка документов */}
        <DocumentsGrid>
          {loading ? (
            <EmptyState>
              <CircularProgress size={40} />
              <Typography variant="body2">
                Загрузка документов...
              </Typography>
            </EmptyState>
          ) : documents.length > 0 ? (
            documents.map((doc) => (
              <DocumentTile
                key={doc.id}
                document={{
                  id: doc.id,
                  name: doc.name,
                  size: doc.size,
                  extension: doc.extension,
                  url: doc.url,
                  uploaded_by: doc.uploaded_by_user,
                  uploaded_at: doc.uploaded_at
                }}
                onDownload={handleDownload}
                onDelete={!isViewOnly && (isOwner || isMember) ? handleDelete : null}
                onShare={!isViewOnly ? handleShare : null}
                isViewOnly={isViewOnly}  // 🔥 ПЕРЕДАЁМ ПРОПС В ДОЧЕРНИЙ КОМПОНЕНТ
              />
            ))
          ) : (
            <EmptyState>
              <Box
                sx={{
                  width: 68,
                  height: 68,
                  borderRadius: '22px',
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(60,110,113,0.07)',
                  border: '1px solid rgba(60,110,113,0.10)',
                  color: '#5B8587',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9)'
                }}
              >
                <FileIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: '#496C6E',
                  mt: 0.5
                }}
              >
                Нет загруженных документов
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 360,
                  textAlign: 'center',
                  mt: -1
                }}
              >
                Добавьте материалы проекта, чтобы они были доступны участникам.
              </Typography>
              {canUpload && (
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={handleUploadClick}
                  sx={{
                    mt: 0.5,
                    borderRadius: 99,
                    borderColor: 'rgba(60,110,113,0.22)',
                    color: '#456F71',
                    background: 'rgba(255,255,255,0.55)',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.4,
                    py: 1,
                    '&:hover': {
                      borderColor: 'rgba(60,110,113,0.38)',
                      background: 'rgba(60,110,113,0.06)'
                    }
                  }}
                >
                  Загрузить первый файл
                </Button>
              )}
            </EmptyState>
          )}
        </DocumentsGrid>
      </DocumentsContainer>

      {/* Диалог загрузки файлов */}
      <Dialog
        open={uploadDialogOpen}
        onClose={loading ? undefined : handleUploadClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '28px',
            background: 'linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(244,249,249,0.93) 100%)',
            border: '1px solid rgba(60,110,113,0.12)',
            boxShadow: '0 28px 80px rgba(25,55,57,0.18), inset 0 1px 0 rgba(255,255,255,0.95)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 260,
              height: 260,
              top: -180,
              right: -100,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(60,110,113,0.16) 0%, rgba(60,110,113,0) 70%)',
              pointerEvents: 'none'
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            position: 'relative',
            zIndex: 1,
            px: 3,
            pt: 3,
            pb: 1.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: '#315A5D', letterSpacing: '-0.02em' }}
              >
                Загрузка документов
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                Добавьте материалы, которые относятся к проекту.
              </Typography>
            </Box>

            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '15px',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                background: 'rgba(60,110,113,0.08)',
                color: '#4F7779',
                border: '1px solid rgba(60,110,113,0.10)'
              }}
            >
              <UploadIcon />
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            position: 'relative',
            zIndex: 1,
            px: 3,
            pt: '8px !important',
            pb: 1
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: '22px',
              background: 'rgba(255,255,255,0.48)',
              border: '1px solid rgba(60,110,113,0.08)'
            }}
          >
            <FileUpload
              onFilesChange={handleFilesUploaded}
              disabled={loading}
              uploadEndpoint={`${API_BASE_URL}/projects/${projectId}/documents/`}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            position: 'relative',
            zIndex: 1,
            px: 3,
            pt: 1.5,
            pb: 2.5,
            gap: 1
          }}
        >
          {loading && (
            <Box
              sx={{
                mr: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: '#527477'
              }}
            >
              <CircularProgress size={17} thickness={4} />
              <Typography variant="body2" fontWeight={600}>
                Загрузка документов…
              </Typography>
            </Box>
          )}

          <Button
            onClick={handleUploadClose}
            disabled={loading}
            sx={{
              minWidth: 100,
              borderRadius: 99,
              px: 2.2,
              py: 1,
              color: '#5B7072',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Отмена
          </Button>
        </DialogActions>
      </Dialog>

      {/* Уведомления */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.type || 'info'}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProjectDocuments;